/**
 * Past rounds this player joined — reachable from the wallet (see Nav.tsx's
 * note: history lives beside the balance it explains, not as a third tab).
 *
 * Winner cartela numbers are shown for every round here, not just the ones
 * the viewer won — the server only returns rounds the caller actually
 * joined, so this is the public outcome of a round they were already part
 * of, not a peek at someone else's game.
 *
 * **No winner names, ever** — real or phantom. A won cartela number and its
 * payout are the public result of a round the caller was in; who actually
 * owns that cartela is not shown to anyone, matching the admin Rounds list
 * (§7.5). The caller's own wins are called out with the "You won" chip.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { errorMessage } from '../api/errors.ts';
import type { RoundHistoryView } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { etbWhole, roundRef } from '../format.ts';

interface PastRoundsProps {
  onBack(): void;
}

export function PastRounds({ onBack }: PastRoundsProps) {
  const t = useT();
  const [rounds, setRounds] = useState<RoundHistoryView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { rounds: next } = await api.roundHistory();
      setRounds(next);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, t));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="screen">
      <header className="roundhead">
        <button type="button" className="roundhead__back" onClick={onBack} aria-label="Back">
          ‹
        </button>
        <h1 className="lobby__title">{t.pastRounds}</h1>
      </header>

      {error && <div className="banner banner--error">{error}</div>}

      {rounds === null && <div className="lobby__empty">{t.connecting}</div>}

      {rounds !== null && rounds.length === 0 && (
        <div className="lobby__empty">
          <p className="lobby__empty-title">{t.pastRoundsEmpty}</p>
        </div>
      )}

      <ul className="pastrounds">
        {(rounds ?? []).map((round) => (
          <PastRoundRow key={round.roundId} round={round} />
        ))}
      </ul>
    </div>
  );
}

function PastRoundRow({ round }: { round: RoundHistoryView }) {
  const t = useT();

  return (
    <li className="pastroundcard">
      <div className="pastroundcard__top">
        <span className="roundcard__ref">
          {round.gameTypeName ? (
            <span className="roundcard__type">{round.gameTypeName}</span>
          ) : null}
          #{roundRef(round.roundId)}
        </span>
        {round.status === 'cancelled' ? (
          <span className="chip">{t.roundCancelled}</span>
        ) : round.won ? (
          <span className="chip chip--live">{t.youWon}</span>
        ) : null}
      </div>

      <p className="pastroundcard__mine">
        {t.myCartelas(round.myCartelaNumbers.join(', '))}
      </p>

      {round.status === 'finished' && round.winners.length > 0 && (
        <ul className="pastroundcard__winners">
          {round.winners.map((w) => (
            <li key={`${round.roundId}-${w.cartelaNumber}`}>
              {t.winnerLine(w.cartelaNumber, etbWhole(w.amount))}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
